import { AxiosError } from "axios";
import {
  Button,
  Container,
  DateInput,
  Form,
  Input,
  Message,
  RadioGroup,
  Stack,
  Text,
} from "basis";
import { RadioGroupOptions } from "components/types";
import {
  validateAuMobileNumber,
  validateName,
  validateNzMobileNumber,
} from "library/formValidation";
import { createRequestLetterTicket } from "library/zendeskAPI";
import {
  closedCCletterTypes,
  openCCletterTypes,
} from "library/zendeskAPI/customFieldsMap/requestLetterCustomFieldsMap";
import { useMutation } from "react-query";
import { CreditCard } from "../CreditCard/CreditCard";
import { Separator } from "../Separator/Separator";
import { FORM_SUBMITTED } from "./constants";
import {
  ApiGetResponse,
  FormValues,
  GetRequestLetterParams,
  ScreenProps,
} from "./types";

const RequestLetterForm = ({
  state: { accountInfo },
  dispatch,
}: ScreenProps) => {
  const token = accountInfo.cardToken;
  const letterTypes: RadioGroupOptions = accountInfo.isClosed
    ? closedCCletterTypes
    : openCCletterTypes;
  const initialValues: FormValues = {
    letterType: letterTypes.length === 1 ? letterTypes[0].value : "",
    firstName: "",
    lastName: "",
    dateOfBirth: {
      day: "",
      month: "",
      year: "",
    },
    mobileNumber: "",
  };

  const createTicketMutation = useMutation<
    ApiGetResponse,
    AxiosError,
    GetRequestLetterParams
  >(createRequestLetterTicket, {
    onSuccess(
      _ticketId,
      { letterType, firstName, lastName, dateOfBirth, mobileNumber },
    ) {
      const formValues: FormValues = {
        letterType,
        firstName,
        lastName,
        dateOfBirth,
        mobileNumber,
      };
      dispatch({
        type: FORM_SUBMITTED,
        formValues,
      });
    },
  });

  const onSubmit = ({ errors, values }) => {
    if (Object.keys(errors).length === 0) {
      createTicketMutation.mutate({
        userName: accountInfo.userName,
        productName: accountInfo.cardName,
        email: accountInfo.userEmail,
        isNZ: accountInfo.isNZ,
        firstName: values.firstName,
        lastName: values.lastName,
        dateOfBirth: values.dateOfBirth,
        mobileNumber: values.mobileNumber,
        last4digits: accountInfo.cardEndsWith,
        letterType: values.letterType,
        token,
      });
    }
  };

  return (
    <Form initialValues={initialValues} onSubmit={onSubmit}>
      <Stack gap="8">
        <Container bg="grey.t05" padding="4">
          <CreditCard
            cardName={accountInfo.cardName}
            cardEndsWith={accountInfo.cardEndsWith}
          />
        </Container>
        <Text
          as="h3"
          textStyle="heading5"
          color="primary.blue.t100"
          margin="4 0 0 0"
        >
          Letter type and details
        </Text>
        <Container width-sm="50%">
          {letterTypes.length === 1 ? (
            <Stack gap="3">
              <Text>
                <strong>Letter type</strong>
              </Text>
              <Text>{letterTypes[0].label}</Text>
            </Stack>
          ) : (
            <RadioGroup
              name="letterType"
              label="Letter type"
              columns={1}
              options={letterTypes}
            />
          )}
        </Container>
        <Separator />
        <Stack gap="3">
          <Text>
            <strong>Email address</strong>
          </Text>
          <Text>{accountInfo.userEmail}</Text>
          <Text textStyle="body2">
            Your letter will be emailed to this address.
          </Text>
        </Stack>
        <Separator />
        <Stack gap="8" width-sm="50%">
          <Input name="firstName" label="First name" validate={validateName} />
          <Input name="lastName" label="Last name" validate={validateName} />
          <DateInput name="dateOfBirth" label="Date of birth" />
          <Input
            type="tel"
            name="mobileNumber"
            label="Mobile number"
            validate={
              accountInfo.isNZ ? validateNzMobileNumber : validateAuMobileNumber
            }
          />
        </Stack>
        {createTicketMutation.isError && (
          <Message severity="blocking" bg="secondary.pink.t30">
            Sorry, there was an error submitting your letter. Please try again
            later.
          </Message>
        )}
        <Button
          type="submit"
          width="260"
          loading={createTicketMutation.isLoading}
        >
          Request letter
        </Button>
      </Stack>
    </Form>
  );
};

export { RequestLetterForm };
